import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Button, Message, Modal, Image, Divider, Icon } from "semantic-ui-react";
import { User } from "../../../api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLanguage } from "../../../contexts";
import logoImg from "../../../assets/svg/isosustemia 1.svg";
import "./InviteComplete.scss";

const userController = new User();

export function InviteComplete() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const { changeLanguage, translations, language } = useLanguage();
  const t = (key) => translations[key] || key;

  const [userData, setUserData] = useState(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [repeatPasswordVisible, setRepeatPasswordVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("appLanguage")) {
      const idiomaNavegador = navigator.language || navigator.userLanguage;
      const idioma = idiomaNavegador.split("-")[0];
      changeLanguage(["es", "en", "pt", "fr"].includes(idioma) ? idioma : "en");
    }
  }, []);

  useEffect(() => {
    userController
      .getInviteByToken(token)
      .then((data) => {
        setUserData(data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsInvalid(true);
        setIsLoading(false);
      });
  }, [token]);

  const formik = useFormik({
    initialValues: { password: "", repeatPassword: "" },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, "Mínimo 6 caracteres")
        .required("Campo obligatorio"),
      repeatPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Las contraseñas deben coincidir")
        .required("Campo obligatorio"),
    }),
    validateOnChange: false,
    onSubmit: async ({ password }) => {
      try {
        setError("");
        await userController.completeInvite(token, { password });
        navigate("/admin");
      } catch (err) {
        setError(err.msg || "Error al completar el registro");
      }
    },
  });

  const renderModalContent = () => {
    if (isLoading) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <div className="ui active centered inline loader" />
        </div>
      );
    }

    if (isInvalid || !userData) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <Message negative>
            <Message.Header>Invitación inválida</Message.Header>
            <p>La invitación ha expirado o es inválida.</p>
          </Message>
        </div>
      );
    }

    return (
      <div className="login-modal-form">
        <p style={{ marginBottom: "1.2rem", color: "#444" }}>
          Bienvenido a Sustemia, <strong>{userData.email}</strong>. Crea tu contraseña para acceder.
        </p>

        <Form onSubmit={formik.handleSubmit} className="login-form">
          <Form.Field error={!!formik.errors.password}>
            <label>Contraseña</label>
            <div className="password-input-wrapper">
              <i className="lock icon password-input-wrapper__lock" />
              <input
                type={passwordVisible ? "text" : "password"}
                name="password"
                placeholder="Contraseña"
                value={formik.values.password}
                onChange={formik.handleChange}
              />
              <button
                type="button"
                className="password-input-wrapper__eye"
                onClick={() => setPasswordVisible((v) => !v)}
              >
                <Icon name={passwordVisible ? "eye slash" : "eye"} />
              </button>
            </div>
            {formik.errors.password && (
              <div className="ui pointing above prompt label">
                {formik.errors.password}
              </div>
            )}
          </Form.Field>

          <Form.Field error={!!formik.errors.repeatPassword}>
            <label>Repetir contraseña</label>
            <div className="password-input-wrapper">
              <i className="lock icon password-input-wrapper__lock" />
              <input
                type={repeatPasswordVisible ? "text" : "password"}
                name="repeatPassword"
                placeholder="Repetir contraseña"
                value={formik.values.repeatPassword}
                onChange={formik.handleChange}
              />
              <button
                type="button"
                className="password-input-wrapper__eye"
                onClick={() => setRepeatPasswordVisible((v) => !v)}
              >
                <Icon name={repeatPasswordVisible ? "eye slash" : "eye"} />
              </button>
            </div>
            {formik.errors.repeatPassword && (
              <div className="ui pointing above prompt label">
                {formik.errors.repeatPassword}
              </div>
            )}
          </Form.Field>

          {error && <Message negative content={error} style={{ marginBottom: "0.75rem" }} />}

          <Button
            type="submit"
            primary
            fluid
            size="large"
            loading={formik.isSubmitting}
          >
            Activar cuenta
          </Button>
        </Form>
      </div>
    );
  };

  return (
    <div className="landing-page">
      {/* Navbar */}
      <nav className="landing-nav">
        <div className="landing-nav__left">
          <Image src={logoImg} className="landing-nav__logo-img" />
          <span className="landing-nav__brand">sustemia</span>
        </div>
        <div className="landing-nav__right">
          <div
            className={`landing-nav__lang-selector${langOpen ? " open" : ""}`}
            onMouseEnter={() => setLangOpen(true)}
            onMouseLeave={() => setLangOpen(false)}
          >
            {langOpen
              ? ["es", "en", "pt"].map((lang) => (
                  <span
                    key={lang}
                    className={`landing-nav__lang-opt${language === lang ? " active" : ""}`}
                    onClick={() => {
                      changeLanguage(lang);
                      setLangOpen(false);
                    }}
                  >
                    {lang.toUpperCase()}
                  </span>
                ))
              : (
                  <span
                    className="landing-nav__lang-opt active"
                    onClick={() => setLangOpen(true)}
                  >
                    {(language || "es").toUpperCase()}
                  </span>
                )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="landing-hero">
        <div className="landing-hero__card">
          <div className="landing-hero__bg-card" />
          <div className="landing-hero__inner-card">
            <h1 className="landing-hero__title">{t("platform_tagline")}</h1>
            <Divider className="landing-hero__divider" />
          </div>
        </div>
      </main>

      {/* Invite Complete Modal */}
      <Modal
        open
        onClose={() => navigate("/admin")}
        size="tiny"
        closeIcon
        className="auth-modal"
      >
        <Modal.Content>
          {renderModalContent()}
        </Modal.Content>
      </Modal>
    </div>
  );
}
